import {
  HistoryOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  RollbackOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Tag as TagDTO } from '@dto/tag.dto';
import { TagValue } from '@dto/tagValue.dto';
import { Button, Dropdown, Input, InputRef, Space, Tooltip, type RefSelectProps } from 'antd';
import {
  ChangeEvent,
  forwardRef,
  KeyboardEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { Log } from 'src/service/log.service';
import { fetchTagValuesByCategory } from '../api/tagApi';
import { FILTER_OPTIONS, HEALTH_STATUS_OPTIONS } from '../constants';
import type {
  SearchComposerDraft,
  SearchComposerDropdowns,
} from '../hooks/useSearchComposer';
import type {
  FilterTag,
  SelectionType,
  SubsystemClassificationItem,
} from '../search.types';
import { useSearchStore } from '../store/useSearchStore';
import {
  HealthStatusSelect,
  TagCategorySelects,
  TagSelect,
  TechnologySelects,
} from './FilterSelects';
import { SearchHelpTooltip } from './SearchHelpTooltip';

import '../styles/SearchInput.less';

export interface SearchInputProps {
  filters: FilterTag[];
  freeText: string;
  draft: SearchComposerDraft;
  dropdowns: SearchComposerDropdowns;
  onFreeTextChange: (inputValue: string) => void;
  onStartDraft: (type: SelectionType, existingFilter?: FilterTag, editIdx?: number) => void;
  onUpdateDraft: (patch: Partial<SearchComposerDraft>) => void;
  onCommitDraft: (patch?: Partial<SearchComposerDraft>) => FilterTag[];
  onCancelDraft: () => void;
  onPromoteFreeTextToSearchFilter: () => void;
  onSetDropdown: (field: keyof SearchComposerDropdowns, value: boolean) => void;
  onLoadFromQuery: (query: string) => void;
  onSubmitSearch?: () => void;
  onClose?: () => void;
  onTabOut?: (direction: 'forward' | 'backward') => void;
  placeholder?: string;
  availableTags?: TagDTO[];
  availableTagsCategories?: TagDTO[];
  availableArchitecture?: SubsystemClassificationItem[];
}

export interface SearchInputHandle {
  focus: () => void;
  focusPrefixStart: () => void;
  focusSuffixEnd: () => void;
}

interface UseSearchInputStateProps {
  availableArchitecture: SubsystemClassificationItem[];
  availableTags: TagDTO[];
  availableTagsCategories: TagDTO[];
  selectedTechnology: string | null;
  selectedTagCategory: string | null;
}

const useSearchInputState = ({
  availableArchitecture,
  availableTags,
  availableTagsCategories,
  selectedTechnology,
  selectedTagCategory,
}: UseSearchInputStateProps) => {
  const [availableCategoryTags, setAvailableCategoryTags] = useState<TagValue[]>([]);
  const loadTagsAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      loadTagsAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!selectedTagCategory) {
      loadTagsAbortRef.current?.abort();
      setAvailableCategoryTags([]);
      return;
    }

    loadTagsAbortRef.current?.abort();
    const abortController = new AbortController();
    loadTagsAbortRef.current = abortController;

    void (async () => {
      try {
        const tags = await fetchTagValuesByCategory(selectedTagCategory, abortController.signal);
        if (!abortController.signal.aborted) {
          setAvailableCategoryTags(tags);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          Log.error('Failed to load tags by category', error);
          setAvailableCategoryTags([]);
        }
      } finally {
        if (loadTagsAbortRef.current === abortController) {
          loadTagsAbortRef.current = null;
        }
      }
    })();
  }, [selectedTagCategory]);

  const technologyOptions = useMemo(() => {
    const classMap = new Map<string, string>();
    const hwTypeMap = new Map<string, string>();
    for (const item of availableArchitecture) {
      if ('class' in item && item.class && !classMap.has(item.class)) {
        classMap.set(item.class, item.classLabel || item.class);
      } else if ('hwType' in item && item.hwType && !hwTypeMap.has(item.hwType)) {
        hwTypeMap.set(item.hwType, item.hwTypeLabel || item.hwType);
      }
    }
    return [
      ...Array.from(classMap.entries()).map(([value, label]) => ({ label, value })),
      ...Array.from(hwTypeMap.entries()).map(([value, label]) => ({ label, value })),
    ].sort((a, b) => a.label.localeCompare(b.label));
  }, [availableArchitecture]);

  const subsystemOptions = useMemo(() => {
    if (!selectedTechnology) return [];
    const uniqueSubsystems = new Map<string, string>();
    for (const item of availableArchitecture) {
      const isMatchingTechnology =
        ('class' in item && item.class === selectedTechnology) ||
        ('hwType' in item && item.hwType === selectedTechnology);
      if (isMatchingTechnology && item.subsystem && !uniqueSubsystems.has(item.subsystem)) {
        uniqueSubsystems.set(item.subsystem, item.subsystemLabel || item.subsystem);
      }
    }
    return Array.from(uniqueSubsystems.entries())
      .map(([value, label]) => ({ label, value }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableArchitecture, selectedTechnology]);

  const sortedTagOptions = useMemo(
    () => [...availableTags].sort((a, b) => a.label.localeCompare(b.label)).map((tag) => ({ label: tag.label, value: tag.label })),
    [availableTags]
  );

  const sortedCategoryOptions = useMemo(
    () => [...availableTagsCategories].sort((a, b) => a.label.localeCompare(b.label)).map((category) => ({ label: category.label, value: category.label })),
    [availableTagsCategories]
  );

  const sortedCategoryTagOptions = useMemo(
    () => [
      { label: 'All', value: '*' },
      ...[...availableCategoryTags].sort((a, b) => (a.value || '').localeCompare(b.value || '')).map((tag) => ({ label: tag.value, value: tag.value })),
    ],
    [availableCategoryTags]
  );

  return { technologyOptions, subsystemOptions, sortedTagOptions, sortedCategoryOptions, sortedCategoryTagOptions };
};

const TEXT_FILTER_SELECTIONS = new Set<SelectionType>([
  'search',
  'regex',
  'ip',
  'mac',
  'wwn',
  'volid',
]);

const findFilterIndexBySelectionType = (
  filters: FilterTag[],
  selectionType: SelectionType
): number => {
  return filters.findIndex((filter) => {
    if (selectionType === 'technology') {
      return filter.type === 'class_subsystem' || filter.type === 'hw_type_subsystem';
    }
    if (selectionType === 'tag_category') {
      return filter.type === 'tag_category_tag';
    }
    return filter.type === selectionType;
  });
};

const SearchInput = forwardRef<SearchInputHandle, SearchInputProps>(
  (
    {
      filters,
      freeText,
      draft,
      dropdowns,
      onFreeTextChange,
      onStartDraft,
      onUpdateDraft,
      onCommitDraft,
      onCancelDraft,
      onPromoteFreeTextToSearchFilter,
      onSetDropdown,
      onLoadFromQuery,
      onSubmitSearch,
      onClose,
      onTabOut,
      placeholder = 'Add filters...',
      availableTags = [],
      availableTagsCategories = [],
      availableArchitecture = [],
    },
    ref
  ) => {
    const historyData = useSearchStore(s => s.historyData);
    const reloadHistoryData = useSearchStore(s => s.reloadHistoryData);
    const navigate = useNavigate();
    const [selectedIndex, setSelectedIndex] = useState(0);

    const inputRef = useRef<InputRef>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const historyBtnRef = useRef<HTMLButtonElement>(null);
    const rollbackBtnRef = useRef<HTMLButtonElement>(null);
    const helpBtnRef = useRef<HTMLButtonElement>(null);
    const selectRef = useRef<RefSelectProps>(null);
    const tagCategorySelectRef = useRef<RefSelectProps>(null);
    const categoryTagSelectRef = useRef<RefSelectProps>(null);
    const technologySelectRef = useRef<RefSelectProps>(null);
    const subsystemSelectRef = useRef<RefSelectProps>(null);
    const healthStatusSelectRef = useRef<RefSelectProps>(null);
    const focusedDraftRef = useRef('');

    const {
      technologyOptions,
      subsystemOptions,
      sortedTagOptions,
      sortedCategoryOptions,
      sortedCategoryTagOptions,
    } = useSearchInputState({
      availableArchitecture,
      availableTags,
      availableTagsCategories,
      selectedTechnology: draft.selectedTechnology,
      selectedTagCategory: draft.selectedTagCategory,
    });

    useEffect(() => {
      void reloadHistoryData();
    }, [reloadHistoryData]);

    const selectedType = draft.type;
    const {
      editingIndex,
      selectedTag,
      selectedTagCategory,
      selectedCategoryTag,
      selectedTechnology,
      selectedSubsystem,
      selectedHealthStatus,
    } = draft;

    const currentTextValue = selectedType === null ? freeText : draft.text;
    const isAnySelectOpen =
      dropdowns.tagSelect ||
      dropdowns.tagCategorySelect ||
      dropdowns.categoryTagSelect ||
      dropdowns.technologySelect ||
      dropdowns.subsystemSelect ||
      dropdowns.healthStatusSelect;

    useEffect(() => {
      const nextSignature = selectedType ? `${selectedType}:${editingIndex ?? 'new'}` : '';
      if (!nextSignature || focusedDraftRef.current === nextSignature) {
        if (!selectedType) {
          focusedDraftRef.current = '';
        }
        return;
      }

      focusedDraftRef.current = nextSignature;
      const focusDelay = editingIndex === null ? 50 : 100;

      if (selectedType === 'tag') {
        setTimeout(() => selectRef.current?.focus(), focusDelay);
        return;
      }
      if (selectedType === 'tag_category') {
        setTimeout(() => tagCategorySelectRef.current?.focus(), focusDelay);
        return;
      }
      if (selectedType === 'technology') {
        setTimeout(() => technologySelectRef.current?.focus(), focusDelay);
        return;
      }
      if (selectedType === 'health_status') {
        setTimeout(() => healthStatusSelectRef.current?.focus(), focusDelay);
        return;
      }

      setTimeout(() => inputRef.current?.focus(), focusDelay);
    }, [editingIndex, selectedType]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          inputRef.current?.focus();
        },
        focusPrefixStart: () => historyBtnRef.current?.focus(),
        focusSuffixEnd: () => helpBtnRef.current?.focus(),
      }),
      []
    );

    const filteredOptions = useMemo(
      () =>
        FILTER_OPTIONS.filter((option) => {
          const isAlreadySelected = findFilterIndexBySelectionType(filters, option.value) >= 0;
          return (
            !isAlreadySelected &&
            (!freeText ||
              option.label.toLowerCase().includes(freeText.toLowerCase()) ||
              option.value.toLowerCase().includes(freeText.toLowerCase()))
          );
        }),
      [filters, freeText]
    );

    const dropdownItems = useMemo(
      () =>
        filteredOptions.map((option, index) => ({
          key: option.value,
          className: index === selectedIndex ? 'xm-search-input__menu-item--selected' : '',
          label: (
            <div
              className='xm-search-input__menu-item'
              data-testid='search-filter-option'
              onClick={(e) => {
                e.stopPropagation();
                const existingIndex = findFilterIndexBySelectionType(filters, option.value);
                if (existingIndex >= 0) {
                  onStartDraft(option.value, filters[existingIndex], existingIndex);
                } else {
                  onStartDraft(option.value);
                }
              }}
            >
              <Space size={3}>
                <span className='xm-filter-badge__type'>{option.label}:</span>
                <span className='xm-search-input__menu-placeholder'>{option.placeholder}</span>
              </Space>
            </div>
          ),
        })),
      [filteredOptions, filters, onStartDraft, selectedIndex]
    );

    const focusInputSoon = useCallback(() => {
      setTimeout(() => inputRef.current?.focus(), 50);
    }, []);

    const handleCancelSelection = useCallback(() => {
      onCancelDraft();
      setSelectedIndex(0);
      focusInputSoon();
    }, [focusInputSoon, onCancelDraft]);

    const handleDropdownSelect = useCallback((type: SelectionType) => {
      const existingIndex = findFilterIndexBySelectionType(filters, type);
      if (existingIndex >= 0) {
        onStartDraft(type, filters[existingIndex], existingIndex);
      } else {
        onStartDraft(type);
      }
      setSelectedIndex(0);
    }, [filters, onStartDraft]);

    const handleTextInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (selectedType === null) {
        onFreeTextChange(value);
      } else {
        onUpdateDraft({ text: value });
      }

      setSelectedIndex(0);

      if (selectedType === null) {
        onSetDropdown('dropdown', value.length > 0);
      }
    }, [onFreeTextChange, onSetDropdown, onUpdateDraft, selectedType]);

    const handleCommitAndRefocus = useCallback((patch?: Partial<SearchComposerDraft>) => {
      onCommitDraft(patch);
      focusInputSoon();
    }, [focusInputSoon, onCommitDraft]);

    const handleAddAction = useCallback(() => {
      if (selectedType === null) {
        onPromoteFreeTextToSearchFilter();
        focusInputSoon();
        return;
      }

      handleCommitAndRefocus();
    }, [
      focusInputSoon,
      handleCommitAndRefocus,
      onPromoteFreeTextToSearchFilter,
      selectedType,
    ]);

    const handleBlur = useCallback(() => {
      setTimeout(() => {
        const activeElement = document.activeElement;
        const isFocusInside =
          wrapperRef.current?.contains(activeElement) ||
          document.querySelector('.ant-select-dropdown')?.contains(activeElement) ||
          activeElement?.closest('.ant-select') !== null;

        if (isFocusInside) return;

        if (selectedType) {
          onCommitDraft();
        } else {
          onSetDropdown('dropdown', false);
        }
      }, 200);
    }, [onCommitDraft, onSetDropdown, selectedType]);

    const handleTagSelectChange = useCallback((tagValue: string) => {
      handleCommitAndRefocus({ selectedTag: tagValue });
    }, [handleCommitAndRefocus]);

    const handleTagCategoryChange = useCallback((categoryValue: string) => {
      onUpdateDraft({
        selectedTagCategory: categoryValue,
        selectedCategoryTag: null,
      });
      onSetDropdown('tagCategorySelect', false);
      onSetDropdown('categoryTagSelect', true);
      setTimeout(() => {
        categoryTagSelectRef.current?.focus();
      }, 50);
    }, [categoryTagSelectRef, onSetDropdown, onUpdateDraft]);

    const handleCategoryTagChange = useCallback((tagValue: string) => {
      handleCommitAndRefocus({
        selectedCategoryTag: tagValue,
      });
    }, [handleCommitAndRefocus]);

    const handleTechnologyChange = useCallback((technologyValue: string) => {
      const isClassBased = availableArchitecture.some(
        (item) => 'class' in item && item.class === technologyValue
      );

      onUpdateDraft({
        selectedTechnology: technologyValue,
        selectedSubsystem: null,
        technologyType: isClassBased ? 'class' : 'hw_type',
      });
      onSetDropdown('technologySelect', false);
      onSetDropdown('subsystemSelect', true);
      setTimeout(() => {
        subsystemSelectRef.current?.focus();
      }, 50);
    }, [availableArchitecture, onSetDropdown, onUpdateDraft]);

    const handleSubsystemChange = useCallback((subsystemValue: string) => {
      handleCommitAndRefocus({
        selectedSubsystem: subsystemValue,
      });
    }, [handleCommitAndRefocus]);

    const handleHealthStatusChange = useCallback((healthStatusValue: string) => {
      handleCommitAndRefocus({
        selectedHealthStatus: healthStatusValue,
      });
    }, [handleCommitAndRefocus]);

    const isAddEnabled = useMemo(() => {
      if (selectedType === null) {
        return freeText.trim().length > 0;
      }
      if (!TEXT_FILTER_SELECTIONS.has(selectedType)) {
        return false;
      }
      return draft.text.trim().length > 0;
    }, [draft.text, freeText, selectedType]);

    const getCurrentPlaceholder = useCallback((): string => {
      if (selectedType) {
        const option = FILTER_OPTIONS.find((filterOption) => filterOption.value === selectedType);
        return option?.placeholder || 'Enter value...';
      }
      return placeholder;
    }, [placeholder, selectedType]);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
      const openSecondarySelect = (dropdown: 'subsystemSelect' | 'categoryTagSelect', ref: React.RefObject<{ focus(): void; } | null>) => {
        onSetDropdown(dropdown, true);
        setTimeout(() => ref.current?.focus(), 50);
      };

      const isDraftCommittable = () =>
        (TEXT_FILTER_SELECTIONS.has(selectedType) && draft.text.trim()) ||
        (selectedType === 'tag' && selectedTag) ||
        (selectedType === 'tag_category' && selectedTagCategory && selectedCategoryTag) ||
        (selectedType === 'technology' && selectedTechnology && selectedSubsystem) ||
        (selectedType === 'health_status' && selectedHealthStatus);

      const handleArrowDown = () => {
        if (isAnySelectOpen) return;
        e.preventDefault();
        if (dropdowns.dropdown && filteredOptions.length > 0) {
          setSelectedIndex((prev) => (prev + 1) % filteredOptions.length);
        } else if (!selectedType && filteredOptions.length > 0) {
          onSetDropdown('dropdown', true);
          setSelectedIndex(0);
        }
      };

      const handleArrowUp = () => {
        if (isAnySelectOpen) return;
        e.preventDefault();
        if (dropdowns.dropdown && filteredOptions.length > 0) {
          setSelectedIndex((prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length);
        }
      };

      const handleEnter = () => {
        if (isAnySelectOpen) return;
        e.preventDefault();
        if (dropdowns.dropdown && filteredOptions.length > 0) {
          handleDropdownSelect(filteredOptions[selectedIndex].value);
          return;
        }
        if (selectedType === 'technology' && selectedTechnology && !selectedSubsystem) {
          openSecondarySelect('subsystemSelect', subsystemSelectRef);
          return;
        }
        if (selectedType === 'tag_category' && selectedTagCategory && !selectedCategoryTag) {
          openSecondarySelect('categoryTagSelect', categoryTagSelectRef);
          return;
        }
        if (selectedType) {
          if (isDraftCommittable()) onCommitDraft();
          return;
        }
        if (freeText.trim() || (!dropdowns.dropdown && filters.length > 0)) {
          onSubmitSearch?.();
        }
      };

      const handleEscape = () => {
        if (isAnySelectOpen) { e.preventDefault(); e.stopPropagation(); return; }
        if (selectedType !== null) { e.preventDefault(); e.stopPropagation(); handleCancelSelection(); return; }
        if (dropdowns.dropdown) { e.preventDefault(); e.stopPropagation(); onSetDropdown('dropdown', false); return; }
        e.preventDefault(); e.stopPropagation(); onClose?.(); // clean state — close the modal
      };

      const handleSpace = () => {
        if (isAnySelectOpen || selectedType === null || TEXT_FILTER_SELECTIONS.has(selectedType)) return;
        e.preventDefault();
        if (selectedType === 'technology' && selectedTechnology && !selectedSubsystem) {
          openSecondarySelect('subsystemSelect', subsystemSelectRef);
          return;
        }
        if (selectedType === 'tag_category' && selectedTagCategory && !selectedCategoryTag) {
          openSecondarySelect('categoryTagSelect', categoryTagSelectRef);
          return;
        }
        if (isDraftCommittable()) onCommitDraft();
      };

      const handleHomeEnd = () => {
        if (isAnySelectOpen) return;
        e.stopPropagation();
        const nativeInput = inputRef.current?.input;
        if (!nativeInput) return;
        e.preventDefault();
        const inputLength = nativeInput.value.length;
        if (!e.shiftKey) {
          const nextPosition = e.key === 'Home' ? 0 : inputLength;
          nativeInput.setSelectionRange(nextPosition, nextPosition);
          return;
        }
        const { selectionStart, selectionEnd, selectionDirection } = nativeInput;
        const anchor =
          selectionStart === selectionEnd
            ? (selectionStart ?? 0)
            : selectionDirection === 'backward'
              ? (selectionEnd ?? inputLength)
              : (selectionStart ?? 0);
        if (e.key === 'Home') {
          nativeInput.setSelectionRange(0, anchor, 'backward');
        } else {
          nativeInput.setSelectionRange(anchor, inputLength, 'forward');
        }
      };

      const handleTab = () => {
        if (isAnySelectOpen) return;
        if (selectedType === 'tag_category' && selectedTagCategory && !selectedCategoryTag) {
          e.preventDefault();
          openSecondarySelect('categoryTagSelect', categoryTagSelectRef);
        } else if (selectedType === 'technology' && selectedTechnology && !selectedSubsystem) {
          e.preventDefault();
          openSecondarySelect('subsystemSelect', subsystemSelectRef);
        }
      };

      switch (e.key) {
        case 'ArrowDown': handleArrowDown(); break;
        case 'ArrowUp': handleArrowUp(); break;
        case 'Enter': handleEnter(); break;
        case 'Escape': handleEscape(); break;
        case ' ': handleSpace(); break;
        case 'Home':
        case 'End': handleHomeEnd(); break;
        case 'Tab': handleTab(); break;
        default: break;
      }
    }, [
      draft.text,
      dropdowns.dropdown,
      filteredOptions,
      filters,
      freeText,
      handleCancelSelection,
      handleDropdownSelect,
      isAnySelectOpen,
      onClose,
      onCommitDraft,
      onSetDropdown,
      onSubmitSearch,
      selectedCategoryTag,
      selectedHealthStatus,
      selectedIndex,
      selectedSubsystem,
      selectedTag,
      selectedTagCategory,
      selectedTechnology,
      selectedType,
    ]);

    const historyRouteQuery = historyData.length > 0 ? String(historyData[0].key) : '';

    return (
      <div className='xm-search-input-wrapper' ref={wrapperRef}>
        <Dropdown
          menu={{ items: dropdownItems }}
          open={dropdowns.dropdown && filteredOptions.length > 0 && editingIndex === null}
          trigger={[]}
          placement='bottomLeft'
        >
          <div className='xm-search-input'>
            <div className='xm-search-input__prefix'>
              <Button
                ref={historyBtnRef}
                type='text'
                size='small'
                icon={<HistoryOutlined />}
                className='xm-search-input__action-btn'
                onClick={() => {
                  if (historyRouteQuery) {
                    navigate(`/search?${historyRouteQuery}`);
                  } else {
                    navigate('/search');
                  }
                  onClose?.();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Tab' && e.shiftKey) {
                    e.preventDefault();
                    onTabOut?.('backward');
                  }
                }}
              />
              <Button
                ref={rollbackBtnRef}
                type='text'
                size='small'
                icon={<RollbackOutlined />}
                className='xm-search-input__action-btn'
                disabled={historyData.length === 0}
                onClick={() => {
                  const lastSearch = historyData[0];
                  if (!lastSearch?.titleText) return;
                  onLoadFromQuery(lastSearch.titleText);
                }}
              />
            </div>
            <div className='xm-search-input__prefix__icon'>
              <SearchOutlined />
            </div>
            <div className='xm-search-input__row'>
              {selectedType !== null && editingIndex === null ? (
                <span className='xm-filter-badge__type'>
                  {FILTER_OPTIONS.find((option) => option.value === selectedType)?.label}:
                </span>
              ) : null}

              {selectedType === 'tag' ? (
                <TagSelect
                  selectRef={selectRef}
                  value={selectedTag}
                  onChange={handleTagSelectChange}
                  options={sortedTagOptions}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  open={dropdowns.tagSelect}
                  onDropdownVisibleChange={(open) => onSetDropdown('tagSelect', open)}
                />
              ) : selectedType === 'tag_category' ? (
                <TagCategorySelects
                  tagCategorySelectRef={tagCategorySelectRef}
                  categoryTagSelectRef={categoryTagSelectRef}
                  selectedTagCategory={selectedTagCategory}
                  selectedCategoryTag={selectedCategoryTag}
                  onCategoryChange={handleTagCategoryChange}
                  onTagChange={handleCategoryTagChange}
                  categoryOptions={sortedCategoryOptions}
                  tagOptions={sortedCategoryTagOptions}
                  categoryOpen={dropdowns.tagCategorySelect}
                  tagOpen={dropdowns.categoryTagSelect}
                  onCategoryDropdownChange={(open) => onSetDropdown('tagCategorySelect', open)}
                  onTagDropdownChange={(open) => onSetDropdown('categoryTagSelect', open)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                />
              ) : selectedType === 'technology' ? (
                <TechnologySelects
                  technologySelectRef={technologySelectRef}
                  subsystemSelectRef={subsystemSelectRef}
                  selectedTechnology={selectedTechnology}
                  selectedSubsystem={selectedSubsystem}
                  onTechnologyChange={handleTechnologyChange}
                  onSubsystemChange={handleSubsystemChange}
                  technologyOptions={technologyOptions}
                  subsystemOptions={subsystemOptions}
                  technologyOpen={dropdowns.technologySelect}
                  subsystemOpen={dropdowns.subsystemSelect}
                  onTechnologyDropdownChange={(open) => onSetDropdown('technologySelect', open)}
                  onSubsystemDropdownChange={(open) => onSetDropdown('subsystemSelect', open)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                />
              ) : selectedType === 'health_status' ? (
                <HealthStatusSelect
                  healthStatusSelectRef={healthStatusSelectRef}
                  value={selectedHealthStatus}
                  onChange={handleHealthStatusChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  options={HEALTH_STATUS_OPTIONS}
                  open={dropdowns.healthStatusSelect}
                  onDropdownVisibleChange={(open) => onSetDropdown('healthStatusSelect', open)}
                />
              ) : (
                <Input
                  ref={inputRef}
                  data-testid='search-input'
                  autoFocus
                  value={currentTextValue}
                  onChange={handleTextInputChange}
                  onKeyDown={handleKeyDown}
                  onClick={() => {
                    if (selectedType === null) {
                      onSetDropdown('dropdown', true);
                    }
                  }}
                  onBlur={handleBlur}
                  placeholder={getCurrentPlaceholder()}
                  variant="borderless"
                  className='xm-search-input__control'
                />
              )}
            </div>
            <div className='xm-search-input__suffix'>
              <Button
                type='text'
                size='small'
                icon={<PlusOutlined />}
                className='xm-search-input__action-btn'
                disabled={!isAddEnabled}
                onClick={handleAddAction}
              />
              <Tooltip title={<SearchHelpTooltip />} placement='bottomRight' trigger={['hover', 'click']} styles={{ root: { maxWidth: 'min(92vw, 640px)' }, container: { whiteSpace: 'normal', wordBreak: 'break-word' } }}>
                <Button
                  ref={helpBtnRef}
                  type='text'
                  size='small'
                  icon={<QuestionCircleOutlined />}
                  className='xm-search-input__action-btn'
                />
              </Tooltip>
            </div>
          </div>
        </Dropdown>
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
